import { Close, Schedule } from "@mui/icons-material";
import { Badge, Box, DialogActions, IconButton, Tooltip, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import * as React from "react";
import { AppFeatureLookup } from "../../App/Constants/AbacConstants";
import { crmLiteUpgradeMessage } from "../../App/Constants/CrmLiteConstants";
import { useFeaturePermission } from "../../App/State/Hooks/abacHooks";
import { MessageTable } from "../ScheduledMessages/MessageTable";
import MessageHistoryDialogState from "./State/MessageHistoryDialogState";

function MessageHistoryDialog({
  isGroup,
  groupId,
  groupName,
  disableButton,
  countShow,
}: {
  groupId: string;
  isGroup: boolean;
  groupName: string;
  disableButton: boolean;
  countShow: number;
}) {
  const state = MessageHistoryDialogState(groupId, isGroup);
  return (
    <Box>
      <ViewMessageHistoryButton
        onClick={state.handlers.handleOpen}
        disabled={disableButton}
        countShow={countShow}
      />
      <Dialog
        id="delivered-message-window"
        aria-labelledby="group-message-history-title"
        open={state.getters.showScheduledMessageDialog}
        onClose={state.handlers.handleClose}
        scroll="paper"
        fullWidth={true}
        maxWidth="lg"
        PaperProps={{
          sx: {
            maxHeight: { xs: "calc(100vh - 1rem)", sm: "calc(100vh - 64px)" },
            height: { xs: "100vh", sm: "90vh" },
            width: { xs: "calc(100% - 1rem)", sm: "calc(100% - 2rem)" },
            m: { xs: "1rem", sm: "2rem" },
          },
        }}
      >
        <DialogTitle
          id="group-message-history-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
          }}
        >
          <Box>Delivered Messages: {groupName}</Box>
          <Box>
            <IconButton
              onClick={state.handlers.handleClose}
              aria-label="Close Delivered Messages Window"
              aria-controls="delivered-message-window"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers={true}
          sx={{
            display: "grid",
            gridTemplateColumns: "100%",
            gridTemplateRows: "auto 1fr",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: { xs: "column", lg: "row" },
              mb: 2,
              gap: "1rem",
            }}
          ></Box>
          <Box
            sx={{
              mb: 0,
              overflow: "auto",
              "& .MuiTableContainer-root": {
                overflowX: "initial",
              },
              "& .MuiTableHead-root .MuiTableCell-root": {
                lineHeight: "1.2",
              },
            }}
          >
            <MessageTable
              id="message-delivered-history-table"
              loading={state.getters.isLoadingScheduledMessages}
              columns={state.getters.columnsNoActionButtons}
              tableData={state.getters.deliveredMessages}
              isEditingGroup={false}
              showActions={false}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Typography data-test="group-message-history-delivered-count">
            Recently delivered: <strong>{state.getters.deliveredMessages.length}</strong> messages
          </Typography>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const ViewMessageHistoryButton = ({
  onClick,
  disabled,
  countShow,
}: {
  onClick: () => void;
  disabled: boolean;
  countShow: number;
}) => {
  const accessMessageGroupButton = useFeaturePermission(AppFeatureLookup.GroupViewScheduledMessages);
  let tooltip = "View Message History";
  if (!accessMessageGroupButton) {
    tooltip = crmLiteUpgradeMessage;
  } else if (disabled) {
    tooltip = "No message history available";
  }
  return (
    <Tooltip title={tooltip} arrow>
      <Box>
        <IconButton
          disabled={!accessMessageGroupButton || disabled}
          onClick={onClick}
          data-test="message-history-button"
        >
          <Badge badgeContent={accessMessageGroupButton ? countShow : undefined} max={999} color="info">
            <Schedule />
          </Badge>
        </IconButton>
      </Box>
    </Tooltip>
  );
};

export default React.memo(MessageHistoryDialog);
